// src/components/mobileOrders.js
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { db } from "../../utils/firebase";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  getDocs,
} from "firebase/firestore";
import {
  updateOrderStatus,
  getRestaurantProfile,
  updateOrderItems,
  placeOrder,
} from "../../utils/api";
import { v4 as uuidv4 } from "uuid";
import {
  FaPlus,
  
  FaSearch,
  FaTimes,
  FaCheck,
  FaEdit,
  FaReceipt,
  
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import "react-datepicker/dist/react-datepicker.css";
import CreateOrderScreen from "./createOrderScreen";

function StatusBadge({ status }) {
  const statusColors = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-800",
    preparing: "bg-blue-100 text-blue-800",
    served: "bg-purple-100 text-purple-800",
  };

  const formattedStatus =
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  const colorClass =
    statusColors[status.toLowerCase()] || "bg-gray-100 text-gray-800";

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-semibold ${colorClass}`}
    >
      {formattedStatus}
    </span>
  );
}

function InvoiceModal({ order, onClose, restaurantName }) {
  if (!order) return null;

  // Calculate totals with discount support (matching Orders.js logic)
  const subtotal = order.subtotal != null 
    ? Number(order.subtotal) 
    : Math.round((order.items || []).reduce((sum, i) => sum + Number(i.price) * (i.qty || 1), 0) * 100) / 100;
  
  const discountPercent = Number(order.discountPercent || 0);
  const discountAmount = Math.round((subtotal * (discountPercent / 100)) * 100) / 100;
  const adjustedSubtotal = Math.round((subtotal - discountAmount) * 100) / 100;
  const tax = Math.round(adjustedSubtotal * 0.05 * 100) / 100;
  const grandTotal = Math.round((adjustedSubtotal + tax) * 100) / 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm max-h-[85vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-gray-800">Invoice</h3>
            <button onClick={onClose} className="text-gray-400">
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        <div className="p-4">
          {/* Restaurant Header */}
          <div className="text-center border-b-2 border-dashed border-gray-300 pb-4 mb-4">
            <h4 className="text-lg font-bold text-gray-800 mb-2">
              {restaurantName || "RESTAURANT"}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div className="text-left">
                <p>Order No: {order.billNumber || order.id?.slice(-5) || "N/A"}</p>
                <p>
                  Date:{" "}
                  {order.createdAt && order.createdAt._seconds
                    ? new Date(order.createdAt._seconds * 1000).toLocaleDateString("en-GB")
                    : new Date().toLocaleDateString("en-GB")}
                </p>
              </div>
              <div className="text-right">
                <p>Table: {order.tableName || order.tableId || "N/A"}</p>
                <p>
                  Time:{" "}
                  {order.createdAt && order.createdAt._seconds
                    ? new Date(order.createdAt._seconds * 1000).toLocaleTimeString("en-US", {
                        hour12: true,
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : new Date().toLocaleTimeString("en-US", {
                        hour12: true,
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                </p>
              </div>
            </div>
          </div>

          {/* Items Header */}
          <div className="border-b-2 border-dashed border-gray-300 pb-2 mb-3">
            <div className="grid grid-cols-4 gap-2 text-sm font-semibold text-gray-700">
              <div>Item</div>
              <div className="text-center">Qty</div>
              <div className="text-center">Rate</div>
              <div className="text-right">Amt</div>
            </div>
          </div>

          {/* Items List */}
          <div className="space-y-2 mb-4">
            {order.items?.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-4 gap-2 py-1 border-b border-gray-100"
              >
                <div className="font-medium text-gray-800 text-sm">
                  {item.name.length > 12
                    ? item.name.substring(0, 12) + "..."
                    : item.name}
                </div>
                <div className="text-center text-sm text-gray-600">
                  {item.qty || 1}
                </div>
                <div className="text-center text-sm text-gray-600">
                  ₹{item.price}
                </div>
                <div className="text-right font-semibold text-gray-800">
                  ₹{((item.qty || 1) * item.price).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          {/* Total Section */}
          <div className="border-t-2 border-dashed border-gray-300 pt-4 space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {discountPercent > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span>Discount ({discountPercent}%)</span>
                <span>-₹{discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center text-sm">
              <span>Tax (5%)</span>
              <span>₹{tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-gray-200">
              <span>Grand Total</span>
              <span className="text-rose-600">₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center pt-4 border-t-2 border-dashed border-gray-300 mt-4">
            <p className="text-sm text-gray-600 font-medium">
              Thank You, Please Visit again
            </p>
            <p className="text-xs text-gray-500 mt-2">Powered by EasiDine</p>
          </div>

          <button className="w-full py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-semibold mt-4">
            Print Invoice
          </button>
        </div>
      </div>
    </div>
  );
}

// function NewOrderReceivedModal({ order, onClose }) {
//   if (!order) return null;

//   return (
//     <motion.div
//       className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//     >
//       <motion.div
//         className="bg-white rounded-2xl shadow-2xl w-full max-w-sm text-center p-6"
//         initial={{ scale: 0.9, y: 20 }}
//         animate={{ scale: 1, y: 0 }}
//         exit={{ scale: 0.9, y: 20 }}
//       >
//         <motion.div
//           className="text-5xl mb-4"
//           animate={{ scale: [1, 1.2, 1] }}
//           transition={{ duration: 0.5 }}
//         >
//           🎉
//         </motion.div>
//         <h2 className="text-2xl font-bold text-gray-800 mb-2">
//           New Order Received!
//         </h2>
//         <p className="text-gray-600 mb-4">
//           Order <strong>#{order.id?.slice(-5)}</strong>
//         </p>
//         <p className="text-sm text-gray-500">
//           {order.tableName || `Table ${order.tableId}`}
//         </p>
//         <button
//           onClick={onClose}
//           className="mt-6 w-full py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-semibold"
//         >
//           Acknowledge
//         </button>
//       </motion.div>
//     </motion.div>
//   );
// }

export default function OrdersScreen({ restaurantId, onOrderScreenOpen }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [newOrderModal, setNewOrderModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [visibleCount, setVisibleCount] = useState(10); // Show More/Less state
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  useEffect(() => {
    return () => {
      // Reset the state when component unmounts
      if (onOrderScreenOpen) {
        onOrderScreenOpen(false);
      }
    };
  }, [onOrderScreenOpen]);
  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    if (onOrderScreenOpen) {
      onOrderScreenOpen(isCreateOrderOpen);
    }
    // Set up real-time listener for orders
    const q = query(
      collection(db, `restaurants/${restaurantId}/orders`),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        // Normalize and coerce numeric fields so invoice code always sees subtotal / discount fields
        const ordersData = querySnapshot.docs.map((doc) => {
          const data = doc.data() || {};
          const items = data.items || [];

          // compute authoritative subtotal from items (2-decimal)
          const computedSubtotal =
            Math.round(
              (items.reduce(
                (s, it) => s + Number(it.price) * (it.qty || 1),
                0
              ) || 0) * 100
            ) / 100;

          // compute discount/tax/total from items + discountPercent
          const dPercent = Number(data.discountPercent || 0);
          const computedDiscountAmount =
            Math.round(computedSubtotal * (dPercent / 100) * 100) / 100;
          const computedTotalBeforeTax =
            Math.round((computedSubtotal - computedDiscountAmount) * 100) / 100;
          
          // store values that UI and invoice will use (always use computed values)
          const subtotal = computedSubtotal;
          const discountPercent = dPercent;
          const discountAmount = computedDiscountAmount;
          const total = computedTotalBeforeTax;

          return {
            id: doc.id,
            ...data,
            items,
            subtotal,
            discountPercent,
            discountAmount,
            total,
            createdAt: data.createdAt,
          };
        });

        // Get table names
        const tablesSnap = await getDocs(
          collection(db, `restaurants/${restaurantId}/tables`)
        );
        const tableMap = {};
        tablesSnap.forEach((doc) => {
          tableMap[doc.id] = doc.data().name || doc.id;
        });

        const enrichedOrders = ordersData.map((order) => ({
          ...order,
          tableName:
            tableMap[order.tableId] || order.tableName || order.tableId,
        }));

        // Check for new orders
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const newOrder = enrichedOrders.find((o) => o.id === change.doc.id);
            if (newOrder) {
              setNewOrderModal(newOrder);
            }
          }
        });

        setOrders(enrichedOrders);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to orders:", err);
        setLoading(false);
      }
    );

    // Fetch restaurant name
    getRestaurantProfile(restaurantId)
      .then((profile) => setRestaurantName(profile.name || ""))
      .catch((err) => console.error("Failed to load restaurant name", err));

    return () => unsubscribe();
  }, [restaurantId, isCreateOrderOpen, onOrderScreenOpen, refreshTrigger]);

  const filteredOrders = orders
    .filter((order) => {
      if (filter === "all") return true;
      return order.status.toLowerCase() === filter.toLowerCase();
    })
    .filter((order) => {
      if (!search) return true;
      return (
        order.id.toLowerCase().includes(search.toLowerCase()) ||
        order.tableName.toLowerCase().includes(search.toLowerCase())
      );
    });

  // Only show up to visibleCount orders
  const visibleOrders = filteredOrders.slice(0, visibleCount);

  async function handleMarkCompleted(orderId) {
    await updateOrderStatus(restaurantId, orderId, "completed");
  }

  async function handleUpdateOrder(updatedItems) {
    try {
      await updateOrderItems(restaurantId, selectedOrder.id, updatedItems);
      setShowUpdateModal(false);
      setSelectedOrder(null);
      setIsCreateOrderOpen(false);
      if (onOrderScreenOpen) onOrderScreenOpen(false);

      // Trigger refresh after successful update
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to update order:", err);
      alert("Failed to update order.");
    }
  }

  async function handleCreateOrder(items, type, input, discountPercent = 0) {
    try {
      await placeOrder({
        restaurantId,
        items,
        tableId:
          type === "table"
            ? `table-${input}`
            : type === "room"
            ? `room-${input}`
            : `parcel-${uuidv4().slice(0, 8)}`,
        tableName:
          type === "table"
            ? `Table ${input}`
            : type === "room"
            ? `Room ${input}`
            : "Parcel Order",
        source: "staff",
        discountPercent: Number(discountPercent || 0),
      });
      setShowCreateModal(false);
      setIsCreateOrderOpen(false);
      if (onOrderScreenOpen) onOrderScreenOpen(false);

      // Trigger refresh after successful order creation
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to create order:", err);
      alert("Failed to create order.");
    }
  }

  const handleOpenUpdateModal = (order) => {
    setSelectedOrder(order);
    setShowUpdateModal(true);
  };

  if (!restaurantId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
          <div className="text-4xl mb-4">🍽️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            No Restaurant Selected
          </h2>
          <p className="text-gray-600">
            Please select a restaurant to view orders
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Orders</h1>
        <p className="text-gray-600">Manage all restaurant orders</p>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order ID or table"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
              filter === "all"
                ? "bg-gradient-to-r from-rose-500 to-amber-500 text-white"
                : "bg-white text-gray-700 border border-gray-200"
            }`}
          >
            All ({orders.length})
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
              filter === "pending"
                ? "bg-gradient-to-r from-rose-500 to-amber-500 text-white"
                : "bg-white text-gray-700 border border-gray-200"
            }`}
          >
            Pending ({orders.filter((o) => o.status === "pending").length})
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
              filter === "completed"
                ? "bg-gradient-to-r from-rose-500 to-amber-500 text-white"
                : "bg-white text-gray-700 border border-gray-200"
            }`}
          >
            Completed ({orders.filter((o) => o.status === "completed").length})
          </button>
        </div>
      </div>

      {/* Create Order Button */}
      <button
        onClick={() => {
          setShowCreateModal(true);
          setIsCreateOrderOpen(true); // Add this line
        }}
        className="w-full py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-semibold mb-6 flex items-center justify-center gap-2"
      >
        <FaPlus /> Create Order
      </button>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rose-500"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleOrders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    # Order:{order.billNumber || order.id?.slice(-5) || "N/A"}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {order.tableName || `Table ${order.tableId}`}
                  </p>
                </div>
                <StatusBadge status={order.status} />
              </div>

              <div className="flex justify-between items-center mb-3">
                <span className="text-lg font-bold text-rose-600">
                  ₹{Math.round((order.total || 0) * 1.05 * 100) / 100}
                </span>
                <span className="text-sm text-gray-500">
                  {order.createdAt && order.createdAt._seconds
                    ? new Date(
                        order.createdAt._seconds * 1000
                      ).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setInvoiceOrder(order)}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm flex items-center justify-center gap-1"
                >
                  <FaReceipt size={12} /> Invoice
                </button>
                <button
                  onClick={() => handleOpenUpdateModal(order)}
                  className="flex-1 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium text-sm flex items-center justify-center gap-1"
                >
                  <FaEdit size={12} /> Update
                </button>
                <button
                  onClick={() => handleMarkCompleted(order.id)}
                  disabled={order.status === "completed"}
                  className={`flex-1 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 
                 ${
                  order.status === "completed"
                 ? "bg-blue-500 text-white"
                : "bg-red-500 text-white hover:bg-red-600"
                } 
                  disabled:opacity-50`}
                >
                  <FaCheck size={12} /> Done
                </button>
              </div>
            </motion.div>
          ))}

          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              No orders found
            </div>
          )}

          {/* Show More / Show Less Buttons */}
          <div className="flex justify-center gap-2 mt-4">
            {visibleCount < filteredOrders.length && (
              <button
                className="px-4 py-2 bg-gradient-to-r from-amber-400 to-rose-400 text-white rounded-full font-semibold text-sm flex items-center gap-1"
                onClick={() => setVisibleCount((c) => c + 10)}
              >
                Show More <FaChevronDown size={12} />
              </button>
            )}
            {visibleCount > 10 && (
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full font-semibold text-sm flex items-center gap-1"
                onClick={() => setVisibleCount(10)}
              >
                Show Less <FaChevronUp size={12} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <InvoiceModal
        order={invoiceOrder}
        onClose={() => setInvoiceOrder(null)}
        restaurantName={restaurantName}
      />

      {/* <NewOrderReceivedModal
        order={newOrderModal}
        onClose={() => setNewOrderModal(null)}
      /> */}
      <CreateOrderScreen
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setIsCreateOrderOpen(false);
          if (onOrderScreenOpen) onOrderScreenOpen(false); // Reset parent state
        }}
        onOpen={() => {
          setIsCreateOrderOpen(true);
          if (onOrderScreenOpen) onOrderScreenOpen(true);
        }}
        onSubmit={handleCreateOrder}
        restaurantId={restaurantId}
        title="Create Order"
        submitText="Place Order"
      />

      <CreateOrderScreen
        show={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedOrder(null);
          setIsCreateOrderOpen(false);
          if (onOrderScreenOpen) onOrderScreenOpen(false); // Reset parent state
        }}
        onOpen={() => {
          setIsCreateOrderOpen(true);
          if (onOrderScreenOpen) onOrderScreenOpen(true);
        }}
        onSubmit={handleUpdateOrder}
        restaurantId={restaurantId}
        title={`Update Order #${selectedOrder?.id?.slice(-5) || ""}`}
        submitText="Update Order"
        initialCart={selectedOrder?.items || []}
        initialType={
          selectedOrder?.roomNumber
            ? "room"
            : selectedOrder?.tableName &&
              selectedOrder.tableName.toLowerCase().includes("table")
            ? "table"
            : "parcel"
        }
        initialValue={
          selectedOrder?.roomNumber ||
          (selectedOrder?.tableName &&
            selectedOrder.tableName.replace(/[^0-9]/g, "")) ||
          ""
        }
      />
    </div>
  );
}
